import { api } from '@/api/client';
import type { OdooTask, OdooUser } from '@/types/odoo';

export interface EasykitConfig {
  plId: number;                        // projectleider ID for EasyKit (Hendrika)
  installerIds: number[];              // installers who belong to the EasyKit driver roster
  fullyEasykitInstallerIds: number[];  // installers whose "tour" summary is suppressed outside Hendrika
}

export interface PlanningResponse {
  tasks: OdooTask[];
  installerMap: Record<number, string | { name: string; isEmployee: boolean; category?: string }>;
  projectleiderMap: Record<number, string>;
  projectNameMap: Record<number, string>;
  werfMap: Record<number, string>;
  soNameMap: Record<number, string>;
  vorderingProgress?: Record<number, { percentage: number; status: string }>;
  easykitConfig?: EasykitConfig;
}

export interface PlanningPreferencesResponse {
  installerRowOrder: number[];
  showAllInstallers: boolean;
  multiWeek: boolean;
}

export async function fetchPlanningTasks(from: string, to: string): Promise<PlanningResponse> {
  return api<PlanningResponse>(`/api/planning?from=${from}&to=${to}`);
}

export async function fetchPlanningPreferences(): Promise<PlanningPreferencesResponse> {
  return api<PlanningPreferencesResponse>('/api/planning/preferences');
}

export async function savePlanningPreferences(
  preferences: { installerRowOrder?: number[]; showAllInstallers?: boolean; multiWeek?: boolean },
): Promise<PlanningPreferencesResponse> {
  // Send only the provided fields so toggling showAll never clobbers the saved
  // order and vice-versa (the backend shallow-merges).
  return api<PlanningPreferencesResponse>('/api/planning/preferences', {
    method: 'PUT',
    body: JSON.stringify(preferences),
  });
}

export async function fetchUsers(): Promise<OdooUser[]> {
  return api<OdooUser[]>('/api/users');
}

export interface SiblingTask {
  id: number;
  name: string;
  date_deadline: string | false;
  x_studio_installateur: number[];
  x_studio_weergeven_in_planning: boolean;
}

export async function fetchSiblings(parentId: number): Promise<{ siblings: SiblingTask[] }> {
  return api<{ siblings: SiblingTask[] }>(`/api/planning/siblings?parent_id=${parentId}`);
}

export async function saveMultiday(payload: {
  source_task_id: number;
  parent_id: number;
  new_dates: string[];
  remove_task_ids: number[];
}): Promise<{ created: number; merged: number; unscheduled: number; errors: string[] }> {
  return api('/api/planning/multiday', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updatePlanningTask(
  taskId: number,
  data: Partial<Pick<OdooTask, 'date_deadline' | 'x_studio_installateur' | 'x_studio_projectleider' | 'x_studio_planningstage'>>
    & { reactivate?: boolean },
): Promise<OdooTask> {
  return api<OdooTask>(`/api/planning`, {
    method: 'POST',
    body: JSON.stringify({ taskId, ...data }),
  });
}

export async function createPlanningTask(data: {
  type: 'feestdag' | 'verlof' | 'vrije_taak';
  date: string;
  installerIds: number[];
  name?: string;
  /** vrije_taak only: «Dit is een interventie». */
  interventie?: boolean;
}): Promise<{ created: number; errors: string[]; projectId?: number; taskId?: number; warning?: string }> {
  return api('/api/planning/tasks', { method: 'POST', body: JSON.stringify(data) });
}

export async function deletePlanningTask(taskId: number): Promise<void> {
  await api(`/api/planning/tasks/${taskId}`, { method: 'DELETE' });
}

export interface ProjectSearchResult {
  id: number;
  name: string;
  projectName: string;
  address: string;
  saleOrderId: number;
  // SO name (e.g. `S26316301`) linked to the matched project.
  // null when the project has no linked sale order.
  sale_order_name: string | null;
}

export async function searchProjects(q: string): Promise<{ results: ProjectSearchResult[] }> {
  return api(`/api/planning/projects/search?q=${encodeURIComponent(q)}`);
}

export interface FaseResult {
  id: number;
  name: string;
  childCount: number;
  stageId: [number, string] | false;
}

export async function fetchProjectFases(projectId: number): Promise<{ fases: FaseResult[] }> {
  return api(`/api/planning/projects/${projectId}/fases`);
}

export interface ArchivedTask {
  id: number;
  name: string;
  parent_id: [number, string] | false;
}

/** Archived ("Verwijderde taken") non-collector planning tasks for a project. The
 *  dashboard tray filters these to the active fase by parent_id. */
export async function fetchArchivedTasks(projectId: number): Promise<{ tasks: ArchivedTask[] }> {
  return api(`/api/planning/projects/${projectId}/archived`);
}

export async function createProjectFase(projectId: number, name: string): Promise<{ id: number; name: string }> {
  return api(`/api/planning/projects/${projectId}/fases`, { method: 'POST', body: JSON.stringify({ name }) });
}

export async function scheduleFase(data: {
  faseId: number; projectId: number; installerIds?: number[]; date: string;
}): Promise<{ id: number; appended: boolean }> {
  return api('/api/planning/schedule', { method: 'POST', body: JSON.stringify(data) });
}

export interface InstallerRecord {
  id: number;
  name: string;
  category: string;
}

export async function fetchInstallers(): Promise<InstallerRecord[]> {
  return api<InstallerRecord[]>('/api/installers');
}
